import { useOptionalEntityOverTimeContext } from '@internal/backstage-plugin-common-library-react';
import { CategoryDef } from '@internal/plugin-watchtower-common';
import ListIcon from '@mui/icons-material/List';
import Drawer from '@mui/material/Drawer';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Grid,
  makeStyles,
  Typography,
  Tabs,
  Tab,
  Box,
} from '@material-ui/core';

import { useGetCategoriesData } from '../../hooks';
import { CategoryData, CategoryScore } from '../../types';
import {
  getRepoBasedChecksStatus,
  getTeamBasedChecksStatus,
  toStandardISO,
} from '../../utils';
import { CategoryDetails } from '../CategoryDetails/CategoryDetails';
import { HierarchicalScorecardView } from '../ScorecardCategories/Hierarchicalscorecardview';
import { ScorecardInfoCard } from '../ScorecardInfoCard';
import {
  buildScorecardHierarchy,
  getCategoriesFailed,
} from './../../utils/scorecardHierarchyUtils';

interface ExtendedCategoryScore extends CategoryScore {
  aggregatedPassed?: number;
  aggregatedTotal?: number;
  refreshPeriodSeconds?: number;
  lastRefreshedDate?: string;
  children?: Array<{
    team_id: string;
    aggregated_category_badge: string;
    aggregated_category_score_percentage: number;
    aggregated_category_score_proportion_dividend: number;
    aggregated_category_score_proportion_denominator: number;
  }>;
}

const CATEGORY_PRIORITY: Record<string, { priority: number; order: number }> = {
  'Java SDK Resource': { priority: 0, order: 1 },
  'Agent Coverage': { priority: 0, order: 2 },
  'Privileged Identity Management': { priority: 0, order: 3 },
  'VM Vulnerability Compliance': { priority: 0, order: 4 },
  'Public IP': { priority: 0, order: 5 },
  'End of Life': { priority: 0, order: 6 },
  'Key Rotation': { priority: 0, order: 7 },
  'Criticality Set': { priority: 1, order: 11 },
  'Subscription Ownership': { priority: 1, order: 12 },
  'Resource Group Tag': { priority: 1, order: 13 },
  'Repository Ownership': { priority: 1, order: 14 },
  'Blackduck Coverage': { priority: 1, order: 15 },
  'Veracode Coverage': { priority: 1, order: 16 },
  'KaaP Governance': { priority: 1, order: 17 },
  'Information Classification Compliance': { priority: 4, order: 21 },
  'CI/CD Success Rate': { priority: 4, order: 22 },
  'Commit Signature Verification': { priority: 4, order: 23 },
  UPM: { priority: 4, order: 24 },
  Velocity: { priority: 8, order: 31 },
  'Orphaned Issues': { priority: 8, order: 32 },
  'Investment Category': { priority: 8, order: 33 },
  KTLO: { priority: 8, order: 34 },
};

const CATEGORY_PARAM = 'category';
const VIEW_MODE_PARAM = 'view';

const formatPeriodLabel = (
  start?: string,
  end?: string,
  aggregateBy: string = 'month',
): string | undefined => {
  const anchor = start ?? end;
  if (!anchor) return undefined;
  const dt = DateTime.fromISO(anchor);
  if (!dt.isValid) return undefined;
  switch (aggregateBy) {
    case 'day':
      return dt.toFormat('MMM d, yyyy');
    case 'week':
      return `W${dt.weekNumber} ${dt.weekYear}`;
    case 'quarter':
      return `Q${dt.quarter} ${dt.year}`;
    case 'month':
    default:
      return dt.toFormat('MMM yyyy');
  }
};

const toCategoryList = (
  categories: CategoryDef[],
  categoriesData: CategoryData[],
  aggregateBy: string = 'month',
) => {
  if (!categories?.length || !categoriesData?.length) {
    return [];
  }

  return categoriesData.map(categoryData => {
    const {
      description,
      applies_to = [],
      check_structure = {},
      refresh_period_seconds,
      last_refreshed_at,
    } = categories.find(category => category.name === categoryData.category) ??
    {};

    const hasPeriods = Array.isArray((categoryData.data as any)?.periods);
    if (categoryData.error || !hasPeriods) {
      return {
        category: categoryData.category,
        score: 'N/A',
        description,
        tier: 'N/A',
        failed: Boolean(categoryData.error),
        checks: [],
        children: [],
      };
    }

    const isRepoBased = applies_to.includes('Repository');

    const lastData = categoryData.data.periods?.slice().reverse()[0];

    if (!lastData || lastData.value.aggregated_category_badge === 'N/A') {
      return {
        category: categoryData.category,
        score: 'N/A',
        description,
        tier: 'N/A',
        checks: [],
        children: [],
        refreshPeriodSeconds: refresh_period_seconds,
        lastRefreshedDate: last_refreshed_at,
        naReason: lastData?.value?.na_reason ?? null,
        periodLabel: lastData
          ? formatPeriodLabel(lastData.start, lastData.end, aggregateBy)
          : undefined,
        periodStart: lastData?.start,
        periodEnd: lastData?.end,
      };
    }

    const children =
      lastData.value.children?.map((child: any) => ({
        team_id: child.team_id,
        aggregated_category_badge: child.aggregated_category_badge,
        aggregated_category_score_percentage:
          child.aggregated_category_score_percentage,
        aggregated_category_score_proportion_dividend:
          child.aggregated_category_score_proportion_dividend === -1
            ? null
            : child.aggregated_category_score_proportion_dividend,
        aggregated_category_score_proportion_denominator:
          child.aggregated_category_score_proportion_denominator === -1
            ? null
            : child.aggregated_category_score_proportion_denominator,
      })) || [];

    return {
      category: categoryData.category,
      score: Number(
        (lastData.value.aggregated_category_score_percentage * 100).toFixed(2),
      ),
      description,
      tier: lastData.value.aggregated_category_badge ?? 'N/A',
      checks:
        isRepoBased || Array.isArray(lastData.value?.repositories)
          ? getRepoBasedChecksStatus(lastData.value, check_structure)
          : getTeamBasedChecksStatus(lastData.value, check_structure),
      currentTeamScorePercentage: lastData.value.category_score_percentage,
      currentTeamPassed: lastData.value.category_score_proportion_dividend,
      currentTeamTotal: lastData.value.category_score_proportion_denominator,
      aggregatedPassed:
        lastData.value.aggregated_category_score_proportion_dividend,
      aggregatedTotal:
        lastData.value.aggregated_category_score_proportion_denominator,
      children,
      refreshPeriodSeconds: refresh_period_seconds,
      lastRefreshedDate: last_refreshed_at,
      naReason: lastData.value.na_reason ?? null,
      periodLabel: formatPeriodLabel(lastData.start, lastData.end, aggregateBy),
      periodStart: lastData.start,
      periodEnd: lastData.end,
    };
  });
};

const useStyles = makeStyles(theme => ({
  headerSection: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerTabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      minWidth: 100,
      fontSize: '0.875rem',
      padding: '6px 16px',
    },
    '& .MuiTabs-indicator': {
      height: 2,
    },
  },
  headerHint: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
  },
  priorityBadge: {
    color: theme.palette.primary.main,
    backgroundColor:
      (theme.palette as any).primaryBackground || 'rgba(25,118,210,0.1)',
    padding: '2px 10px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  },
  noDataText: {
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.action.hover,
    padding: '2px 10px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  },
  detailsContainer: {
    width: '70%',
    backgroundColor: theme.palette.background.default,
    padding: 15,
  },
  sectionHeader: {
    width: '100%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  needFocusText: {
    color: theme.palette.error.main,
    backgroundColor: 'rgba(211,47,47,0.1)',
    padding: '2px 10px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  },
}));

type AggregationPeriod = 'week' | 'month' | 'quarter';

export const getTimeForAggregation = (
  aggregation: AggregationPeriod,
): { startDate: string; endDate: string } => {
  const now = DateTime.now();
  let startDate: DateTime;
  let endDate: DateTime;

  switch (aggregation) {
    case 'week':
      startDate = now.startOf('week');
      endDate = now.endOf('week');
      break;
    case 'month':
      startDate = now.startOf('month');
      endDate = now.endOf('month');
      break;
    case 'quarter':
      startDate = now.startOf('quarter');
      endDate = now.endOf('quarter');
      break;
    default:
      startDate = now.startOf('day');
      endDate = now.endOf('day');
  }

  return {
    startDate: toStandardISO(startDate),
    endDate: toStandardISO(endDate),
  };
};

interface TeamScorecardSummaryProps {
  categories?: CategoryDef[];
  categoriesData?: CategoryData[];
  title?: string;
  entity?: any;
  teamTreeData?: any;
}

interface TeamScorecardSummaryGroupProps {
  categories: CategoryDef[];
  categoriesData: CategoryData[];
  title: string;
  entityForContext?: any;
  teamTreeData?: any;
}

const TeamScorecardSummaryGroup = ({
  categories,
  categoriesData,
  title,
  entityForContext,
  teamTreeData,
}: TeamScorecardSummaryGroupProps) => {
  const classes = useStyles();
  const timeContext = useOptionalEntityOverTimeContext();

  const { startDate, endDate, aggregateBy } = useMemo(() => {
    if (!timeContext) {
      return {
        startDate: toStandardISO(
          DateTime.now().minus({ months: 5 }).startOf('month'),
        ),
        endDate: toStandardISO(DateTime.now().endOf('month')),
        aggregateBy: 'month',
      };
    }

    return {
      startDate:
        timeContext.dateRange?.start ??
        toStandardISO(DateTime.now().minus({ months: 5 }).startOf('month')),
      endDate:
        timeContext.dateRange?.end ??
        toStandardISO(DateTime.now().endOf('month')),
      aggregateBy: timeContext.aggregateBy ?? 'month',
    };
  }, [timeContext]);

  const [refreshKey, setRefreshKey] = useState(0);

  const { value: refreshedCategoriesData } = useGetCategoriesData(
    teamTreeData,
    startDate,
    endDate,
    aggregateBy,
    refreshKey,
  );

  const activeCategoriesData = refreshedCategoriesData || categoriesData;

  const performDataRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const categoryList = useMemo(
    () =>
      toCategoryList(
        categories,
        activeCategoriesData,
        aggregateBy,
      ) as ExtendedCategoryScore[],
    [categories, activeCategoriesData, aggregateBy],
  );

  const latestPeriodLabel = useMemo(() => {
    let latestStart: string | undefined;
    let latestEnd: string | undefined;
    (activeCategoriesData ?? []).forEach((categoryData: CategoryData) => {
      const periods = (categoryData.data as any)?.periods;
      if (!Array.isArray(periods) || !periods.length) return;
      const last = periods[periods.length - 1];
      if (!latestEnd || (last?.end && last.end > latestEnd)) {
        latestEnd = last?.end;
        latestStart = last?.start;
      }
    });
    return formatPeriodLabel(latestStart, latestEnd, aggregateBy);
  }, [activeCategoriesData, aggregateBy]);

  const hierarchicalScores = useMemo(() => {
    return buildScorecardHierarchy(categoryList);
  }, [categoryList]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategoryName = searchParams.get(CATEGORY_PARAM);

  const selectedCategory = useMemo<ExtendedCategoryScore | undefined>(() => {
    if (!selectedCategoryName) return undefined;
    return categoryList.find(cat => cat.category === selectedCategoryName);
  }, [selectedCategoryName, categoryList]);

  const detailsOpen = Boolean(selectedCategory);

  const closeDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(CATEGORY_PARAM);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const onCardClick = useCallback(
    (card: ExtendedCategoryScore) => {
      if (card.score === 'N/A') return;

      setSearchParams(
        prev => {
          const currentCategory = prev.get(CATEGORY_PARAM);
          if (currentCategory === card.category) return prev;

          const next = new URLSearchParams(prev);
          next.set(CATEGORY_PARAM, card.category);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const priorityGroups = useMemo(() => {
    const getPriorityLabel = (priority: number): string => {
      if (priority <= 0) return 'Critical Priority';
      if (priority <= 3) return 'High Priority';
      if (priority <= 7) return 'Medium Priority';
      return 'Low Priority';
    };

    const groups = new Map<string, ExtendedCategoryScore[]>();
    const other: ExtendedCategoryScore[] = [];
    const notApplicable: ExtendedCategoryScore[] = [];

    categoryList.forEach(cat => {
      if (cat.score === 'N/A') {
        notApplicable.push(cat);
        return;
      }
      const info = CATEGORY_PRIORITY[cat.category];
      if (info) {
        const label = getPriorityLabel(info.priority);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label)!.push(cat);
      } else {
        other.push(cat);
      }
    });

    groups.forEach(group => {
      group.sort((a, b) => {
        const infoA = CATEGORY_PRIORITY[a.category];
        const infoB = CATEGORY_PRIORITY[b.category];
        const priorityDiff =
          (infoA?.priority ?? 999) - (infoB?.priority ?? 999);
        if (priorityDiff !== 0) return priorityDiff;
        const orderDiff = (infoA?.order ?? 999) - (infoB?.order ?? 999);
        if (orderDiff !== 0) return orderDiff;
        if (a.score === 'N/A' && b.score !== 'N/A') return 1;
        if (b.score === 'N/A' && a.score !== 'N/A') return -1;
        if (typeof a.score === 'number' && typeof b.score === 'number') {
          return a.score - b.score;
        }
        return 0;
      });
    });

    other.sort((a, b) => a.category.localeCompare(b.category));
    notApplicable.sort((a, b) => a.category.localeCompare(b.category));

    const orderedLabels = [
      'Critical Priority',
      'High Priority',
      'Medium Priority',
      'Low Priority',
    ].filter(label => groups.has(label));

    return { groups, other, orderedLabels, notApplicable };
  }, [categoryList]);

  const areasFailed = useMemo(() => {
    return getCategoriesFailed(hierarchicalScores);
  }, [hierarchicalScores]);

  const viewMode =
    (searchParams.get(VIEW_MODE_PARAM) as 'flat' | 'grouped') || 'flat';

  const handleViewModeChange = useCallback(
    (_: React.ChangeEvent<{}>, newValue: 'flat' | 'grouped') => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(VIEW_MODE_PARAM, newValue);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  return (
    <Box padding={3}>
      <Box className={classes.headerSection}>
        <ListIcon />
        <Typography variant="h6">
          {title}
          {latestPeriodLabel ? ` (${latestPeriodLabel})` : ''}
        </Typography>

        <Box className={classes.headerTabs}>
          <Tabs
            value={viewMode}
            onChange={handleViewModeChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Flat" value="flat" />
            <Tab label="Grouped" value="grouped" />
          </Tabs>
        </Box>
      </Box>

      <Typography variant="body2" className={classes.headerHint}>
        Each scorecard below shows the score for the most recent period in the
        selected range
        {latestPeriodLabel ? ` (${latestPeriodLabel})` : ''}, not an average of
        the whole range.
      </Typography>

      {selectedCategory && selectedCategory.score !== 'N/A' && (
        <Drawer
          open={detailsOpen}
          onClose={closeDrawer}
          anchor="right"
          PaperProps={{ className: classes.detailsContainer }}
        >
          <CategoryDetails
            categoryScore={selectedCategory}
            onClose={closeDrawer}
            entity={entityForContext}
            performDataRefresh={performDataRefresh}
          >
            {selectedCategory.children}
          </CategoryDetails>
        </Drawer>
      )}

      <Box pt={1}>
        {viewMode === 'grouped' ? (
          <HierarchicalScorecardView
            hierarchicalScores={hierarchicalScores}
            onCardClick={onCardClick}
          />
        ) : (
          <Grid container spacing={3}>
            {areasFailed.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.sectionHeader}>
                  Failed to Load
                  <span className={classes.needFocusText}>
                    {areasFailed.length}
                  </span>
                </Typography>

                <Grid container spacing={2}>
                  {areasFailed.map((categoryScore: CategoryScore) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={categoryScore.category}
                    >
                      <ScorecardInfoCard
                        categoryScore={categoryScore}
                        onCardClick={() => onCardClick(categoryScore)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {priorityGroups.orderedLabels.map(label => (
              <Grid item xs={12} key={label}>
                <Typography variant="caption" className={classes.sectionHeader}>
                  {label}
                  <span className={classes.priorityBadge}>
                    {priorityGroups.groups.get(label)!.length}
                  </span>
                </Typography>

                <Grid container spacing={2}>
                  {priorityGroups.groups.get(label)!.map(cat => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={cat.category}>
                      <ScorecardInfoCard
                        categoryScore={cat}
                        onCardClick={() => onCardClick(cat)}
                        isHighPriority={
                          label === 'Critical Priority' ||
                          label === 'High Priority'
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}

            {priorityGroups.other.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.sectionHeader}>
                  Other
                  <span className={classes.noDataText}>
                    {priorityGroups.other.length}
                  </span>
                </Typography>

                <Grid container spacing={2}>
                  {priorityGroups.other.map(cat => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={cat.category}>
                      <ScorecardInfoCard
                        categoryScore={cat}
                        onCardClick={() => onCardClick(cat)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {priorityGroups.notApplicable.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.sectionHeader}>
                  Not Applicable
                  <span className={classes.noDataText}>
                    {priorityGroups.notApplicable.length}
                  </span>
                </Typography>

                <Grid container spacing={2}>
                  {priorityGroups.notApplicable.map(cat => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={cat.category}>
                      <ScorecardInfoCard
                        categoryScore={cat}
                        onCardClick={() => onCardClick(cat)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export const TeamScorecardSummary = ({
  categories: propCategories,
  categoriesData: propCategoriesData,
  title = 'Scorecards List',
  entity: propEntity,
  teamTreeData: propTeamTreeData,
}: TeamScorecardSummaryProps = {}) => {
  if (!propCategories || !propCategoriesData) {
    return null;
  }

  return (
    <TeamScorecardSummaryGroup
      categories={propCategories}
      categoriesData={propCategoriesData}
      title={title}
      entityForContext={propEntity ?? null}
      teamTreeData={propTeamTreeData ?? null}
    />
  );
};
